/**
 * Rand formatting, in one place.
 *
 * This was inlined at a dozen call sites in two incompatible locales. `en-ZA`
 * groups with a space and uses a comma for the decimal (`R26 398,40`) where
 * `en-US` gives `R26,398.40` — so the same figure read differently depending on
 * which page you were looking at, and the two are easy to mistake for each
 * other at a glance. The PA surfaces standardise on `en-US`.
 *
 * `—` rather than `R0.00` for a missing value: an em-dash reads as "no figure",
 * where a zero reads as a real amount of nothing.
 */

export type FormatRandOptions = {
  /** Show cents. Off for headline figures, on for anything reconcilable. */
  cents?: boolean;
};

export function formatRand(
  value: number | string | null | undefined,
  options: FormatRandOptions = {},
): string {
  if (value === null || value === undefined || value === "") return "—";
  const numeric = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(numeric)) return "—";

  const digits = options.cents === false ? 0 : 2;
  return `R${numeric.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
}

/**
 * Split a formatted amount so the cents can be set smaller than the rands.
 *
 * The figures strip renders `R26,398` at 23px with `.40` trailing at 15px: the
 * magnitude is what you scan, the cents are what you reconcile, and giving them
 * the same weight makes every figure look longer than it is. Returns the cents
 * with their separator attached, or an empty string when there are none.
 */
export function splitRand(value: number | string | null | undefined): {
  main: string;
  cents: string;
} {
  const formatted = formatRand(value);
  const dot = formatted.lastIndexOf(".");
  if (dot === -1) return { main: formatted, cents: "" };
  return { main: formatted.slice(0, dot), cents: formatted.slice(dot) };
}
